
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/material';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 600,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2, 3),
  },
}));

const REASONS = [
  { value: 'STOCK_COUNT',        label: 'Stock count' },
  { value: 'DATA_ENTRY_MISTAKE', label: 'Data entry mistake' },
  { value: 'DAMAGED',            label: 'Damaged' },
  { value: 'EXPIRED',            label: 'Expired' },
  { value: 'THEFT',              label: 'Theft / missing' },
  { value: 'OTHER',              label: 'Other' },
];

export default function AdjustStockDialog({
  open,
  onClose,
  row,
  onSubmit,
  onGoToRestock,
}) {
  const currentQty = Number(row?.quantity ?? 0);

  const [mode, setMode] = React.useState('SET'); // SET | DELTA
  const [newQty, setNewQty] = React.useState('');
  const [delta, setDelta] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [note, setNote] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setMode('SET');
    setNewQty('');
    setDelta('');
    setReason('');
    setNote('');
    setSubmitting(false);
    // console.log(row);
  }, [open, row?.id]);

  const sharedFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      backgroundColor: '#f3f3f6',
      '& fieldset': { border: 'none' },
      '& input': {
        color: '#333',
        fontSize: '1rem',
        fontWeight: 500,
        '&::placeholder': { color: '#888', opacity: 1 },
      },
    },
  };

  const baseLabelSx = { display: 'block', mb: 1, fontWeight: 500 };

  const hasSetValue   = mode === 'SET'   && String(newQty).trim() !== '';
const hasDeltaValue = mode === 'DELTA' && String(delta).trim() !== '';

const parsedNewQty =
  mode === 'SET'
    ? (hasSetValue ? Number(newQty) : NaN)
    : (hasDeltaValue ? currentQty + Number(delta) : NaN);

const parsedDelta =
  mode === 'SET'
    ? (hasSetValue && Number.isFinite(parsedNewQty) ? parsedNewQty - currentQty : NaN)
    : (hasDeltaValue ? Number(delta) : NaN);

  // ✅ change: separate "error" vs "limit reached"
  const atZero   = Number.isFinite(parsedNewQty) && parsedNewQty === 0;
const belowZero = Number.isFinite(parsedNewQty) && parsedNewQty < 0;
const errorText = belowZero ? `Stock quantity cannot go below 0.` : '';
const limitText = atZero ? `This will set the stock to 0.` : '';

const helper =
  belowZero ? errorText :
  atZero ? limitText :
  '';

  
  const invalidNumber =
    !Number.isFinite(parsedNewQty) ||
    parsedNewQty < 0 ||
    !Number.isFinite(parsedDelta) ||
    parsedDelta === 0;

  const isIncreasing = Number.isFinite(parsedDelta) && parsedDelta > 0;

  // const xe = 'xe'
  const canSubmit =
    !!row?.id &&
    !!reason &&
    !invalidNumber &&
    !submitting;

  const handleSave = async () => {
    if (!canSubmit) {
        console.log('cants');
        
    }

    setSubmitting(true);
    try {
      await onSubmit({
        inventoryId: row.id,
        mode,
        newQty: parsedNewQty,
        delta: parsedDelta,
        reason,
        note: note?.trim() || '',
        });
      onClose?.();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BootstrapDialog onClose={onClose} open={open} aria-labelledby="adjust-stock-title">
      <DialogTitle
        sx={{ m: 0, p: 2, fontSize: '1.25rem', fontWeight: 600 }}
        id="adjust-stock-title"
      >
        Adjust Stock
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Box
          sx={{
            mb: 3,
            p: 1.5,
            borderRadius: 1,
            bgcolor: '#fff7ed',
            border: '1px solid #f59e0b',
            display: 'flex',
            gap: 1.25,
            alignItems: 'flex-start',
          }}
        >
          <WarningAmberIcon sx={{ color: '#f59e0b', mt: 0.25 }} />
          <Box>
            <Typography sx={{ color: '#92400e', fontWeight: 600 }}>
              Use this only if the system quantity is wrong.
            </Typography>
            <Typography variant="body2" sx={{ color: '#92400e' }}>
              This is for stock corrections only. New stock received should be added from <b>Add Stock</b> or <b>Restock</b>. All corrections are logged with a reason.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, color: '#111827' }}>
            {row?.medicineName || '—'}
          </Typography>
          <Typography sx={{ color: 'gray', fontWeight: 400, fontSize: '0.8rem', mt: 0.15 }}>
            {row?.genericName || '—'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            Batch: <b>{row?.batchNumber || '—'}</b> • Current qty: <b>{currentQty}</b>
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ width: '45%' }}>
            <Typography component="label" sx={baseLabelSx}>
              Correction type
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              sx={sharedFieldSx}
            >
              <MenuItem value="SET">Set new quantity</MenuItem>
              <MenuItem value="DELTA">Increase / decrease by</MenuItem>
            </TextField>
          </Grid>

          {mode === 'SET' ? (
            <Grid item xs={12} sm={6} style={{ marginLeft: 'auto', width: '45%' }}>
              <Typography component="label" sx={baseLabelSx}>
                New quantity
              </Typography>
              <TextField
                type="number"
                slotProps={{
                    htmlInput : { min: 0, step: 1 },
                    formHelperText : {
    sx: {
      color: belowZero ? '#dc2626' : '#d97706', // red / amber
      fontWeight: 500,
      marginLeft: 0,
    },
                }}}
                placeholder="e.g. 42"
                fullWidth
                size="small"
                value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
                sx={sharedFieldSx}
                error={belowZero}
                helperText={helper || ' '}
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} style={{ marginLeft: 'auto', width: '45%' }}>
              <Typography component="label" sx={baseLabelSx}>
                Change by (+ / -)
              </Typography>
              <TextField
                slotProps={{
                htmlInput : {
                  min: -currentQty,
                  step: 1,
                },
                formHelperText : {
    sx: {
      color: belowZero ? '#dc2626' : '#d97706', // red / amber
      fontWeight: 500,
      marginLeft: 0,
    },
                }
                }}
                type="number"
                placeholder="e.g. -5 or 10"
                fullWidth
                size="small"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                sx={sharedFieldSx}
                error={belowZero}
                helperText={helper || ' '}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6} sx={{ width: '45%' }}>
            <Typography component="label" sx={baseLabelSx}>
              Result
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={
                Number.isFinite(parsedNewQty) && Number.isFinite(parsedDelta)
                  ? `${parsedNewQty}  (${parsedDelta > 0 ? '+' : ''}${parsedDelta})`
                  : '—'
              }
              disabled
              sx={sharedFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} sx={{ marginLeft: 'auto', width: '45%' }}>
            <Typography component="label" sx={baseLabelSx}>
              Reason <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={sharedFieldSx}
            >
              {REASONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
            <Typography component="label" sx={baseLabelSx}>
              Note (optional)
            </Typography>
            <TextField
              placeholder="Short note…"
              fullWidth
              size="small"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={sharedFieldSx}
            />
          </Grid>

          {isIncreasing && (
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 1,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: '#eef6ff',
                  border: '1px solid #1976d2',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
                  This correction increases stock. If you received new stock for this batch, use <b>Restock</b> instead.
                </Typography>

                {onGoToRestock && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onGoToRestock}
                    sx={{ textTransform: 'none', boxShadow: 'none' }}
                  >
                    Restock
                  </Button>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSubmit}
          sx={{ textTransform: 'none' }}
        >
          Confirm correction
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
